import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { GlobeHemisphereWest, Scan, SpinnerGap, Warning, Users, Eye } from "@phosphor-icons/react";

function riskColor(s) { return s >= 70 ? "#FF3B30" : s >= 40 ? "#FF9F0A" : "#32D74B"; }

export default function GlobalWatch() {
  const [examples, setExamples] = useState([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/intel/public-cameras").then((r) => setExamples(r.data)).catch(() => {}); }, []);

  const analyze = async (imageUrl) => {
    if (!imageUrl) { toast.error("Paste a public camera image URL"); return; }
    setBusy(true); setResult(null); setActive(imageUrl);
    try {
      const { data } = await api.post("/intel/analyze", { image_url: imageUrl });
      setResult(data.result);
      toast.success("Public feed analyzed");
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
      <div className="xl:col-span-5 space-y-4">
        <div className="bg-[#121214] border border-white/10 p-4">
          <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1 flex items-center gap-2">
            <GlobeHemisphereWest size={16} weight="bold" className="text-[#007AFF]" /> Analyze Any Public Camera
          </h2>
          <p className="text-xs text-[#A1A1AA] leading-relaxed mb-4">
            Point SentryVision's AI at any <span className="text-[#3395FF]">public</span> traffic/street camera snapshot URL — for crowd, queue, and incident awareness around your premises or anywhere. Uses only public feeds you're permitted to access.
          </p>
          <label className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1.5">Public Snapshot URL</label>
          <input data-testid="global-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…/camera-snapshot.jpg"
            className="w-full bg-[#1A1A1E] border border-white/10 px-3 py-2.5 text-sm font-mono-data outline-none focus:border-[#007AFF] transition-colors mb-3" />
          <button data-testid="global-analyze" onClick={() => analyze(url)} disabled={busy}
            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white py-2.5 font-display font-bold uppercase tracking-wide text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <SpinnerGap size={16} className="animate-spin" /> : <Scan size={16} weight="bold" />} {busy ? "Analyzing" : "Analyze Feed"}
          </button>
        </div>

        <div className="bg-[#121214] border border-white/10 p-4">
          <h3 className="font-display font-black uppercase tracking-tighter text-xs text-[#A1A1AA] mb-3">Example Public Feeds</h3>
          <div className="grid grid-cols-1 gap-2">
            {examples.map((c) => (
              <button key={c.url} data-testid={`example-${c.name}`} onClick={() => analyze(c.url)}
                className="flex items-center gap-3 border border-white/10 hover:border-[#007AFF] p-2 text-left transition-colors">
                <img src={c.url} alt={c.name} className="w-16 h-12 object-cover" />
                <div><p className="text-sm">{c.name}</p><p className="font-mono-data text-[10px] text-[#71717A]">{c.location}</p></div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="xl:col-span-7">
        {!result && !busy && (
          <div className="h-full min-h-[400px] border border-dashed border-white/10 flex flex-col items-center justify-center text-[#71717A]">
            <Eye size={40} weight="thin" /><p className="mt-3 text-sm">Analysis of the public feed appears here</p>
          </div>
        )}
        {busy && (
          <div className="h-full min-h-[400px] border border-white/10 flex flex-col items-center justify-center text-[#007AFF]">
            <SpinnerGap size={40} className="animate-spin" /><p className="mt-3 text-sm font-mono-data uppercase tracking-widest">Processing public feed…</p>
          </div>
        )}
        {result && (
          <div data-testid="global-result" className="space-y-4">
            {active && <img src={active} alt="feed" className="w-full aspect-video object-cover border border-white/10" />}
            <div className="bg-[#121214] border border-white/10 p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-display font-black uppercase tracking-tighter text-sm">Scene Assessment</h2>
                <span className="font-mono-data text-xs px-2 py-1 border uppercase" style={{ color: riskColor(result.theft_risk?.score || 0), borderColor: riskColor(result.theft_risk?.score || 0) + "66" }}>Threat: {result.threat_level}</span>
              </div>
              <p className="text-sm text-[#E4E4E7] leading-relaxed">{result.scene_summary}</p>
              <div className="flex gap-4 mt-3 font-mono-data text-xs text-[#A1A1AA]">
                <span className="flex items-center gap-1"><Users size={13} /> {result.people_count} people</span>
                <span className="flex items-center gap-1"><Warning size={13} /> risk {result.theft_risk?.score ?? 0}</span>
              </div>
            </div>
            {(result.body_language_signals?.length > 0) && (
              <div className="bg-[#121214] border border-white/10 p-4">
                <h3 className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Observations</h3>
                <ul className="space-y-1.5">
                  {result.body_language_signals.map((s, i) => <li key={i} className="text-sm text-[#E4E4E7] flex gap-2"><span className="text-[#007AFF]">›</span>{s}</li>)}
                </ul>
              </div>
            )}
            <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4">
              <h3 className="text-xs uppercase tracking-widest text-[#3395FF] mb-1">Recommended Action</h3>
              <p className="text-sm text-white">{result.recommended_action}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
